import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import Header from '../components/Header';

const BASE_URL = 'https://www.watreatmentfinder.com';

const BED_TYPES = [
  ['eval_treatment_adult',    'Eval & Treatment (Adult)'],
  ['eval_treatment_youth',    'Eval & Treatment (Youth)'],
  ['eval_treatment_clip',     'Eval & Treatment (CLIP)'],
  ['crisis_stabilization',    'Crisis Stabilization'],
  ['intensive_bh',            'Intensive BH Treatment'],
  ['competency_restoration',  'Competency Restoration'],
  ['mh_peer_respite',         'MH Peer Respite'],
  ['withdrawal_adult',        'Withdrawal Management (Adult)'],
  ['withdrawal_youth',        'Withdrawal Management (Youth)'],
  ['secure_withdrawal_adult', 'Secure Withdrawal (Adult)'],
  ['secure_withdrawal_youth', 'Secure Withdrawal (Youth)'],
  ['res_sud_adult',           'Residential SUD (Adult)'],
  ['res_sud_youth',           'Residential SUD (Youth)'],
];

function Code({ children }) {
  return (
    <pre className="bg-gray-900 text-gray-100 text-xs leading-relaxed rounded-lg px-4 py-3 overflow-x-auto">
      <code>{children}</code>
    </pre>
  );
}

function Inline({ children }) {
  return <code className="text-xs bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded px-1.5 py-0.5">{children}</code>;
}

function Section({ id, title, children }) {
  return (
    <section id={id} className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl px-6 py-5">
      <h2 className="text-base font-bold text-gray-900 dark:text-gray-100 mb-3">{title}</h2>
      <div className="space-y-3 text-sm text-gray-600 dark:text-gray-400 leading-relaxed">{children}</div>
    </section>
  );
}

function Endpoint({ method, path, children }) {
  const color = method === 'GET'
    ? 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-800'
    : 'bg-green-50 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-800';
  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 space-y-3">
      <div className="flex items-center gap-2 flex-wrap">
        <span className={`text-xs font-bold px-2 py-0.5 rounded border ${color}`}>{method}</span>
        <code className="text-sm text-gray-900 dark:text-gray-100">{path}</code>
      </div>
      {children}
    </div>
  );
}

export default function ApiDocs() {
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col">
      <Helmet>
        <title>Availability API | WA BHA Treatment Finder</title>
        <meta name="description" content="Push real-time bed availability from your EHR or census system to WA BHA Treatment Finder." />
      </Helmet>
      <Header right={null} />

      <div className="flex-1 max-w-4xl mx-auto w-full px-4 py-10">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Availability API</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 max-w-2xl">
            A small, authenticated API that lets a facility's own systems — an EHR, a census tool,
            or a scheduled script — report current bed availability and referral status directly to
            its listing. Updates appear on the facility card immediately and trigger bed alerts for
            case managers watching for openings.
          </p>
        </div>

        <div className="space-y-5">
          <Section id="overview" title="How it works">
            <p>
              Every facility listing has a licensed bed count for each bed type, sourced from the WA
              Department of Health Greenbook. The API lets you report how many of those beds are
              <em> open right now</em>, and whether the facility is currently accepting referrals.
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Base URL: <Inline>{BASE_URL}/api/v1</Inline></li>
              <li>All requests and responses are JSON.</li>
              <li>Each API key is tied to exactly one facility and can only read or update that facility.</li>
              <li>Send as often as your census changes. Most integrations post on every admission and discharge, or on a schedule (e.g. hourly).</li>
              <li>Every update is timestamped and logged. Listings show "updated X ago" and are flagged as possibly out of date after 7 days without an update.</li>
            </ul>
          </Section>

          <Section id="auth" title="Authentication">
            <p>
              Keys are generated by a verified organization account from <strong>My Facility → API Access</strong>,
              or by the site administrator on a facility's behalf. A key is shown once at creation; only a
              hash is stored. Keys can be revoked at any time.
            </p>
            <p>Send the key as a bearer token on every request:</p>
            <Code>{`Authorization: Bearer bha_live_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx`}</Code>
            <p>
              Requests without a valid key return <Inline>401</Inline>. Each key is limited to 60 requests per minute
              (<Inline>429</Inline> when exceeded).
            </p>
          </Section>

          <Section id="endpoints" title="Endpoints">
            <Endpoint method="GET" path="/api/v1/bed-types">
              <p>Public. Returns the list of bed type identifiers accepted by the API.</p>
              <Code>{`[
  { "type": "res_sud_adult", "label": "Residential SUD (Adult)" },
  { "type": "withdrawal_adult", "label": "Withdrawal Management (Adult)" },
  ...
]`}</Code>
            </Endpoint>

            <Endpoint method="GET" path="/api/v1/availability">
              <p>Returns the current state of the key's facility, including licensed counts and the last reported availability for each bed type the facility is licensed for.</p>
              <Code>{`{
  "facility": { "id": 599, "name": "Example Recovery Center", "city": "Yakima", "county": "Yakima" },
  "accepting_referrals": true,
  "beds": [
    { "type": "res_sud_adult",     "label": "Residential SUD (Adult)",       "licensed": 16, "available": 3 },
    { "type": "withdrawal_adult",  "label": "Withdrawal Management (Adult)", "licensed": 8,  "available": null }
  ],
  "updated_at": "2026-10-20T15:42:10.000Z"
}`}</Code>
              <p><Inline>available</Inline> is <Inline>null</Inline> when nothing has been reported for that bed type.</p>
            </Endpoint>

            <Endpoint method="POST" path="/api/v1/availability">
              <p>
                Reports availability. Send <Inline>beds</Inline>, <Inline>accepting_referrals</Inline>, or both.
                <Inline>PUT</Inline> is accepted as an alias.
              </p>
              <Code>{`{
  "beds": {
    "res_sud_adult": 3,
    "withdrawal_adult": 0
  },
  "accepting_referrals": true
}`}</Code>
              <ul className="list-disc pl-5 space-y-1">
                <li><Inline>beds</Inline> is a complete snapshot: it replaces whatever was reported before. Include every bed type you track on each call.</li>
                <li>Values are whole numbers from 0 up to the facility's licensed count for that type. A value of 0 means no beds open.</li>
                <li>Bed types the facility is not licensed for are rejected with <Inline>400</Inline> and an explanation.</li>
                <li>The response is the same shape as <Inline>GET /api/v1/availability</Inline>, reflecting the saved state.</li>
              </ul>
              <p>Validation errors look like:</p>
              <Code>{`{
  "error": "Invalid bed availability.",
  "details": [
    "\\"res_sud_adult\\" cannot exceed the licensed count of 16."
  ]
}`}</Code>
            </Endpoint>
          </Section>

          <Section id="bed-types" title="Bed type identifiers">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                    <th className="py-2 pr-4">type</th>
                    <th className="py-2">Bed type</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {BED_TYPES.map(([type, label]) => (
                    <tr key={type}>
                      <td className="py-1.5 pr-4"><Inline>{type}</Inline></td>
                      <td className="py-1.5 text-gray-700 dark:text-gray-300">{label}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>

          <Section id="examples" title="Examples">
            <p className="font-medium text-gray-700 dark:text-gray-300">curl</p>
            <Code>{`curl -X POST ${BASE_URL}/api/v1/availability \\
  -H "Authorization: Bearer $BHA_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{"beds":{"res_sud_adult":3},"accepting_referrals":true}'`}</Code>
            <p className="font-medium text-gray-700 dark:text-gray-300">PowerShell</p>
            <Code>{`Invoke-RestMethod -Method Post \`
  -Uri "${BASE_URL}/api/v1/availability" \`
  -Headers @{ Authorization = "Bearer $env:BHA_API_KEY" } \`
  -ContentType "application/json" \`
  -Body '{"beds":{"res_sud_adult":3},"accepting_referrals":true}'`}</Code>
            <p className="font-medium text-gray-700 dark:text-gray-300">Node.js</p>
            <Code>{`const res = await fetch('${BASE_URL}/api/v1/availability', {
  method: 'POST',
  headers: {
    'Authorization': \`Bearer \${process.env.BHA_API_KEY}\`,
    'Content-Type': 'application/json',
  },
  body: JSON.stringify({ beds: { res_sud_adult: 3 }, accepting_referrals: true }),
});
console.log(await res.json());`}</Code>
          </Section>

          <Section id="getting-started" title="Getting a key">
            <ol className="list-decimal pl-5 space-y-1">
              <li>Create an organization account and request a link to your facility listing from the Account page.</li>
              <li>Once the link is approved, open <strong>My Facility</strong> and generate a key under <strong>API Access</strong>.</li>
              <li>Store the key somewhere safe (an environment variable or your integration platform's secrets store) and start posting.</li>
            </ol>
            <p>
              Questions, or building an integration for an EHR vendor? Reach out through the{' '}
              <Link to="/faq" className="text-blue-600 dark:text-blue-400 hover:underline font-medium">FAQ page</Link>.
            </p>
          </Section>
        </div>
      </div>
    </div>
  );
}
